import React, { useState, useEffect } from 'react';
import { Heart, Loader2, Check, Sparkles } from 'lucide-react';
import { apiClient } from '@/lib/api-client';
import { formatCurrency } from '@/lib/utils';

interface TipStaffSectionProps {
  onSkip: () => void;
  onComplete: (tipAmount: number) => void;
  scBalance: number;
}

const TIP_OPTIONS = [1, 5, 10, 20, 50, 100];

function getDefaultTipAmount(balance: number): number {
  if (balance > 1000) return 100;
  if (balance > 500) return 50;
  if (balance > 300) return 20;
  if (balance > 200) return 10;
  if (balance > 110) return 5;
  if (balance > 101) return 1;
  return 0;
}

export function TipStaffSection({ onSkip, onComplete, scBalance }: TipStaffSectionProps) {
  const [selectedAmount, setSelectedAmount] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [remainingBalance, setRemainingBalance] = useState(scBalance);

  useEffect(() => {
    const defaultTip = getDefaultTipAmount(scBalance);
    if (defaultTip > 0 && defaultTip <= scBalance) {
      setSelectedAmount(defaultTip);
    }
  }, [scBalance]);

  const affordableOptions = TIP_OPTIONS.filter((amount) => amount <= scBalance);
  
  if (affordableOptions.length === 0) {
    return null;
  }

  const handleTip = async () => {
    if (!selectedAmount || selectedAmount > scBalance || loading) return;

    setLoading(true);
    setError(null);

    try {
      await apiClient.post('/redemption/tip', { scAmount: selectedAmount });
      
      try {
        const balanceResponse = await apiClient.get('/redemption/balance');
        if (balanceResponse?.balance) {
          const scBonus = parseFloat(String(balanceResponse.balance.sc_bonus_amount || 0)) || 0;
          const scPromo = parseFloat(String(balanceResponse.balance.sc_promotional_amount || 0)) || 0;
          const scRedeemable = parseFloat(String(balanceResponse.balance.sc_redeemable_amount || 0)) || 0;
          setRemainingBalance(Math.round((scBonus + scPromo + scRedeemable) * 100) / 100);
        }
      } catch {
        setRemainingBalance(scBalance - selectedAmount);
      }
      
      setSuccess(true);
      setTimeout(() => {
        onComplete(selectedAmount);
      }, 1500);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to process tip');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="p-5 sm:p-6 text-center">
        <div className="relative inline-block mb-4">
          <div className="absolute inset-0 bg-pink-500/30 blur-2xl rounded-full animate-pulse"></div>
          <div className="relative w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-pink-400 to-rose-600 rounded-full flex items-center justify-center shadow-lg shadow-pink-500/30">
            <Check className="h-8 w-8 sm:h-10 sm:w-10 text-white" strokeWidth={3} />
          </div>
        </div>
        <h3 className="text-xl sm:text-2xl font-black text-white mb-2">Thank You!</h3>
        <p className="text-pink-400 font-medium mb-3">Your generosity means the world to our team</p>
        <div className="bg-gradient-to-br from-pink-500/20 to-rose-500/10 border border-pink-500/30 rounded-2xl p-4 sm:p-5 mb-4 backdrop-blur-sm">
          <div className="text-3xl sm:text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-rose-400">
            ${selectedAmount?.toFixed(2)}
          </div>
          <div className="text-sm text-pink-400/80 mt-1">tip sent to staff</div>
        </div>
        <p className="text-gray-400 text-sm">
          Remaining balance: <span className="text-white font-bold">{formatCurrency(remainingBalance)} SC</span>
        </p>
      </div>
    );
  }

  return (
    <div className="p-5 sm:p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2.5">
          <div className="relative">
            <div className="absolute inset-0 bg-pink-500/40 blur-lg rounded-lg"></div>
            <div className="relative bg-gradient-to-br from-pink-400 to-rose-500 p-2 rounded-lg">
              <Heart className="h-5 w-5 text-white fill-white" />
            </div>
          </div>
          <div>
            <h3 className="text-base sm:text-lg font-bold text-white">Tip the Team</h3>
            <p className="text-xs text-gray-400">Help us keep your 24/7 support available</p>
          </div>
        </div>
      </div>

      <div className="bg-gradient-to-br from-pink-500/5 to-rose-500/5 border border-pink-500/20 rounded-xl p-3 mb-4">
        <div className="flex items-center gap-2 text-sm text-gray-300">
          <Sparkles className="h-4 w-4 text-pink-400 flex-shrink-0" />
          <span>Your tips help our team stay motivated to assist you around the clock</span>
        </div>
      </div>

      <div className="text-xs font-semibold text-gray-400 mb-2 uppercase tracking-wide">Select tip amount</div>
      <div className="grid grid-cols-3 gap-2 mb-4">
        {affordableOptions.map((amount) => {
          const isSelected = selectedAmount === amount;
          const isDefault = amount === getDefaultTipAmount(scBalance);
          return (
            <button
              key={amount}
              onClick={() => setSelectedAmount(amount)}
              className={`relative min-h-[64px] p-3 rounded-xl border-2 transition-all duration-200 active:scale-[0.97] text-center ${
                isSelected
                  ? 'border-pink-500 bg-gradient-to-br from-pink-500/15 to-rose-500/10 shadow-lg shadow-pink-500/10'
                  : 'border-gray-700/80 hover:border-gray-600 bg-gray-800/40 hover:bg-gray-800/60'
              }`}
            >
              {isDefault && !isSelected && (
                <div className="absolute -top-2 left-1/2 -translate-x-1/2 bg-gradient-to-r from-pink-500 to-rose-500 text-white text-[8px] font-bold uppercase px-1.5 py-0.5 rounded-full whitespace-nowrap">
                  Suggested
                </div>
              )}
              <div className={`font-black text-lg ${isSelected ? 'text-pink-400' : 'text-white'}`}>
                ${amount}
              </div>
              <div className={`text-xs ${isSelected ? 'text-pink-400/70' : 'text-gray-500'}`}>
                {amount} SC
              </div>
            </button>
          );
        })}
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3 mb-4">
          <p className="text-red-400 text-sm font-medium">{error}</p>
        </div>
      )}

      <div className="flex gap-3">
        <button
          onClick={onSkip}
          className="flex-1 min-h-[48px] bg-gray-800/80 hover:bg-gray-700 text-gray-300 font-medium py-3 rounded-xl transition-all active:scale-[0.98]"
        >
          Skip
        </button>
        <button
          onClick={handleTip}
          disabled={!selectedAmount || selectedAmount > scBalance || loading}
          className={`flex-1 min-h-[48px] py-3 rounded-xl font-bold transition-all active:scale-[0.98] flex items-center justify-center gap-2 ${
            selectedAmount && selectedAmount <= scBalance
              ? 'bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-400 hover:to-rose-400 text-white shadow-lg shadow-pink-500/20'
              : 'bg-gray-700 text-gray-500 cursor-not-allowed'
          }`}
        >
          {loading ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            <>
              <Heart className="h-4 w-4" />
              Send Tip
            </>
          )}
        </button>
      </div>
    </div>
  );
}
